/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Stack } from 'react-bootstrap';
import { diagnosticLogBurst, downloadLocalDatabase, restartBackendServer, uploadLocalDatabase } from '../../data';
import { StatusContext } from '../StatusContext';
import { SettingsContext } from '../SettingsContext';
import styled, { useTheme } from 'styled-components';

const DiagnosticsShell = styled.div<{ $panelBg: string; $panelText: string; $panelBorderColor: string; $mutedText: string; $buttonBg: string; $buttonText: string }>`
    .diag-file-input {
        background-color: ${(p) => p.$panelBg};
        color: ${(p) => p.$panelText};
        border-color: ${(p) => p.$panelBorderColor};
    }

    .diag-file-input::file-selector-button,
    .diag-file-input::-webkit-file-upload-button {
        background-color: ${(p) => p.$buttonBg};
        color: ${(p) => p.$buttonText};
        border: 1px solid ${(p) => p.$panelBorderColor};
        border-radius: 0.35rem;
        padding: 0.375rem 0.75rem;
        margin-right: 0.75rem;
        cursor: pointer;
    }

    .diag-file-input:disabled::file-selector-button,
    .diag-file-input:disabled::-webkit-file-upload-button {
        opacity: 0.65;
        cursor: not-allowed;
    }

    .diag-file-input::file-selector-button:hover,
    .diag-file-input::-webkit-file-upload-button:hover {
        filter: brightness(1.08);
    }

    .diag-muted {
        color: ${(p) => p.$mutedText};
    }
`;

function DiagnosticsSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const statusContext = useContext<any>(StatusContext);
    const settingsContext = useContext(SettingsContext);
    const [isRunning, setIsRunning] = useState(false);
    const [isRestarting, setIsRestarting] = useState(false);
    const [isDownloadingDb, setIsDownloadingDb] = useState(false);
    const [isUploadingDb, setIsUploadingDb] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [showRestartConfirm, setShowRestartConfirm] = useState(false);
    const [showUploadConfirm, setShowUploadConfirm] = useState(false);
    const [resultMsg, setResultMsg] = useState<string>('');
    const panelBg = theme?.colors?.headerBg || '#1d2730';
    const panelText = theme?.colors?.text || '#fff';
    const panelBorder = `1px solid ${theme?.colors?.ctext || '#5aff5a'}`;
    const panelBorderColor = theme?.colors?.ctext || '#5aff5a';
    const mutedText = theme?.colors?.textMuted || '#9aa0a6';
    const fileBtnBg = theme?.colors?.headerBg || '#27313a';
    const fileBtnText = theme?.colors?.text || '#fff';
    const isErrorResult = resultMsg.startsWith('Failed') || resultMsg.toLowerCase().includes('error');

    useEffect(() => {
        settingsContext.setIsDirty(false);
    }, [settingsContext]);

    async function runLogDiagnostics() {
        setIsRunning(true);
        setResultMsg('');
        try {
            const rc = await diagnosticLogBurst();
            const sent = Number(rc?.sent || 0);
            setResultMsg(`Queued ${sent} diagnostic log messages.`);
            statusContext.postInfo(`Diagnostic test burst queued (${sent} messages).`);
        } catch (err: any) {
            const msg = `Failed to queue diagnostic messages: ${err.message}`;
            setResultMsg(msg);
            statusContext.postError(msg);
        } finally {
            setIsRunning(false);
        }
    }

    async function runBackendRestart() {
        setIsRestarting(true);
        setShowRestartConfirm(false);
        setResultMsg('Restart requested. Waiting for backend disconnect/restart...');
        try {
            const rc = await restartBackendServer();
            const msg = rc?.message || 'Backend restart requested.';
            statusContext.postError(msg);
            setResultMsg(msg);
        } catch (err: any) {
            const msg = `Backend restart request sent (connection may drop): ${err?.message || err}`;
            statusContext.postError(msg);
            setResultMsg(msg);
        } finally {
            setIsRestarting(false);
        }
    }

    async function runDownloadLocalDb() {
        setIsDownloadingDb(true);
        setResultMsg('');
        try {
            await downloadLocalDatabase();
            const msg = 'Downloaded local database backup (vacuumed + trimmed).';
            statusContext.postInfo(msg);
            setResultMsg(msg);
        } catch (err: any) {
            const msg = `Failed to download local database: ${err.message}`;
            statusContext.postError(msg);
            setResultMsg(msg);
        } finally {
            setIsDownloadingDb(false);
        }
    }

    async function runUploadLocalDb() {
        if (!uploadFile) {
            const msg = 'Choose a local SQLite DB file before uploading.';
            statusContext.postError(msg);
            setResultMsg(msg);
            return;
        }
        setIsUploadingDb(true);
        setShowUploadConfirm(false);
        setResultMsg('Uploading local DB. Backend will restart after successful import...');
        try {
            const rc = await uploadLocalDatabase(uploadFile);
            const msg = rc?.message || 'Local DB uploaded. Backend restart requested.';
            statusContext.postError(msg);
            setResultMsg(msg);
        } catch (err: any) {
            const msg = `Upload sent (backend may be restarting): ${err?.message || err}`;
            statusContext.postError(msg);
            setResultMsg(msg);
        } finally {
            setIsUploadingDb(false);
        }
    }

    return (
        <DiagnosticsShell
            $panelBg={panelBg}
            $panelText={panelText}
            $panelBorderColor={panelBorderColor}
            $mutedText={mutedText}
            $buttonBg={fileBtnBg}
            $buttonText={fileBtnText}
        >
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Diagnostics</h3>
                    <div className="text-muted">Run quick UI diagnostics for the log popup and message rendering.</div>
                </div>
            </div>

            <div className="mb-4">
                <div className="fw-semibold mb-2">Log Popup Test</div>
                <div className="text-muted small mb-3">
                    Sends a short burst of websocket log messages, including simulated errors, to validate popup behavior.
                </div>
                <Stack direction="horizontal" gap={2}>
                    <Button variant="primary" disabled={isRunning} onClick={runLogDiagnostics}>
                        {isRunning ? 'Running...' : 'Run Log Diagnostic Burst'}
                    </Button>
                </Stack>
            </div>

            <div className="mb-4">
                <div className="fw-semibold mb-2">Backend Restart</div>
                <div className="text-muted small mb-3">
                    Requests the backend process to exit quickly. If running under systemd or another supervisor,
                    it should be restarted automatically.
                </div>
                <Stack direction="horizontal" gap={2}>
                    <Button variant="danger" disabled={isRestarting} onClick={() => setShowRestartConfirm(true)}>
                        {isRestarting ? 'Requesting Restart...' : 'Restart Backend Server'}
                    </Button>
                </Stack>
            </div>

            <div className="mb-4">
                <div className="fw-semibold mb-2">Local Database (local.db)</div>
                <div className="text-muted small mb-3">
                    Download a compact local SQLite backup (trimmed runtime rows and vacuumed), or upload a local DB.
                    Uploading a replacement local DB will restart the backend server.
                </div>
                <Stack direction="horizontal" gap={2} className="mb-2 flex-wrap">
                    <Button variant="outline-primary" disabled={isDownloadingDb} onClick={runDownloadLocalDb}>
                        {isDownloadingDb ? 'Preparing Download...' : 'Download Local DB'}
                    </Button>
                    <Form.Control
                        className="diag-file-input"
                        type="file"
                        accept=".db,.sqlite,.sqlite3,application/octet-stream,application/x-sqlite3,application/vnd.sqlite3"
                        onChange={(evt) => {
                            const file = evt.target.files && evt.target.files.length > 0 ? evt.target.files[0] : null;
                            setUploadFile(file);
                        }}
                        style={{ maxWidth: '360px' }}
                    />
                    <Button
                        variant="warning"
                        disabled={!uploadFile || isUploadingDb}
                        onClick={() => setShowUploadConfirm(true)}
                    >
                        {isUploadingDb ? 'Uploading...' : 'Upload Local DB'}
                    </Button>
                </Stack>
                {uploadFile ? (
                    <div className="small diag-muted">
                        Selected file: {uploadFile.name}
                    </div>
                ) : null}
            </div>

            {resultMsg ? (
                <Alert
                    variant={isErrorResult ? 'danger' : 'success'}
                    className="py-2"
                    style={{
                        backgroundColor: panelBg,
                        border: panelBorder,
                        color: panelText,
                        fontFamily: 'monospace',
                    }}
                >
                    {resultMsg}
                </Alert>
            ) : null}

            <Modal show={showRestartConfirm} onHide={() => setShowRestartConfirm(false)} centered>
                <Modal.Header closeButton style={{ backgroundColor: panelBg, color: panelText, borderBottom: panelBorder }}>
                    <Modal.Title style={{ color: theme?.colors?.warning || '#ffc107' }}>Restart Backend Server</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: panelBg, color: panelText }}>
                    <div className="mb-2">
                        Restart backend server now?
                    </div>
                    <div style={{ color: mutedText, fontSize: '0.9rem' }}>
                        The UI may disconnect briefly while the process exits and restarts under systemd.
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: panelBg, borderTop: panelBorder }}>
                    <Button variant="outline-secondary" onClick={() => setShowRestartConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" disabled={isRestarting} onClick={runBackendRestart}>
                        {isRestarting ? 'Requesting Restart...' : 'Confirm Restart'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showUploadConfirm} onHide={() => setShowUploadConfirm(false)} centered>
                <Modal.Header closeButton style={{ backgroundColor: panelBg, color: panelText, borderBottom: panelBorder }}>
                    <Modal.Title style={{ color: theme?.colors?.warning || '#ffc107' }}>Upload Local Database</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: panelBg, color: panelText }}>
                    <div className="mb-2">
                        Replace current local database with selected file?
                    </div>
                    <div style={{ color: mutedText, fontSize: '0.9rem' }}>
                        The backend server will exit immediately after upload so it can restart with the new local DB.
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: panelBg, borderTop: panelBorder }}>
                    <Button variant="outline-secondary" onClick={() => setShowUploadConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" disabled={isUploadingDb || !uploadFile} onClick={runUploadLocalDb}>
                        {isUploadingDb ? 'Uploading...' : 'Upload And Restart'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </DiagnosticsShell>
    );
}

export default DiagnosticsSettings;
